import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { ChevronDownIconComponent, ChevronUpIconComponent } from '@itero/ui-toolkit-angular/icons';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { Procedure } from '@shared/models/procedure';
import { TranslateModule } from '@ngx-translate/core';

@Component({
	selector: 'rx-procedure-button',
	templateUrl: './rx-procedure-button.component.html',
	styleUrls: ['./rx-procedure-button.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, ChevronDownIconComponent, ChevronUpIconComponent, TranslateModule]
})
export class RxProcedureButtonComponent extends BaseDestroyableComponent {
	@Input() selectedProcedure: Procedure | null = null;
	@Input() disabled = false;
	/** Order read-only: neutral styling (white surface, gray text, no blue). */
	@Input() readOnly = false;
	@Input() canExpand = true;
	@Input() expanded = false;

	@Input() animationElement: ElementRef<HTMLElement> | undefined = undefined;
	@Input() animationHeight: string | undefined = undefined;
	@Input() animationWidth: string | undefined = undefined;
	@Input() animationTop: string | undefined = undefined;
	@Input() animationLeft: string | undefined = undefined;

	@Output() buttonClicked = new EventEmitter<void>();

	constructor(private readonly hostElementRef: ElementRef<HTMLElement>) {
		super();
	}

	onButtonClick(): void {
		if (this.isInteractive) {
			this.buttonClicked.emit();
		}
	}

	get isInteractive(): boolean {
		return this.canExpand && !this.disabled && !this.readOnly;
	}

	get expandedLeft(): string | undefined {
		return this.getExpandedSize(this.animationLeft, el => el.offsetLeft - this.hostElementRef.nativeElement.offsetLeft, '0px');
	}

	get expandedTop(): string | undefined {
		return this.getExpandedSize(this.animationTop, el => el.offsetTop - this.hostElementRef.nativeElement.offsetTop, '100%');
	}

	get expandedHeight(): string | undefined {
		return this.getExpandedSize(this.animationHeight, el => el.clientHeight, '90vh');
	}

	get expandedWidth(): string | undefined {
		return this.getExpandedSize(this.animationWidth, el => el.clientWidth, '90vw');
	}

	private getExpandedSize(
		customValue: string | undefined,
		refElementGetter: (element: HTMLElement) => number,
		defaultValue: string
	): string | undefined {
		if (!this.expanded) {
			return undefined;
		}
		if (customValue) {
			return customValue;
		}
		if (this.animationElement) {
			return `${refElementGetter(this.animationElement.nativeElement)}px`;
		}

		return defaultValue;
	}
}
